import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { $dashboardSpendingSummaryStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, cn, DateUtils, PaletteUtils } from '@shared/utils';
import { View } from '@widgets';
import { Select, Text } from '@shared/widgets';
import styles from './dashboardSpendingCurrency.module.scss';

const getNumberPostfix = (num: number, locale: string = 'en'): string => {
  if (locale === 'ru') return '-го';

  const mod = num % 10;
  const thDates = [11, 12, 13];
  if (thDates.includes(num)) return 'th';
  switch (mod) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

interface DashboardSpendingCurrencyProps {
  className?: string;
}

export const DashboardSpendingCurrency = (props: DashboardSpendingCurrencyProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardSpendingSummaryStore = useUnit($dashboardSpendingSummaryStore.store);

  const { t, i18n } = useTranslation('dashboard');
  const dates = useMemo(() => DateUtils.getLastMonths({ locale: i18n.language }), [i18n.language]);

  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [date, setDate] = useState<string>(dates[0].value);

  const symbol = useCurrencySymbol(currency);

  useEffect(() => {
    if (currency) $dashboardSpendingSummaryStore.events.getSpending({ currency, date });
    return () => $dashboardSpendingSummaryStore.events.clear();
  }, [currency, date]);

  const colors = useMemo(() => {
    if (PaletteUtils.isPersonalPalette) return PaletteUtils.spendingByCurrencyPersonalColors;
    if (PaletteUtils.isVcfcPalette) return PaletteUtils.spendingByCurrencyVcfcColors;
    return PaletteUtils.spendingByCurrencyInsigniaColors;
  }, []);
  const options = useMemo<ApexOptions>(() => {
    return {
      chart: {
        id: 'bar',
        width: '100%',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        toolbar: {
          show: false,
        },
      },
      noData: {
        text: t('card.spendingCurrency.noData'),
        style: {
          color: 'var(--text-slate)',
        },
      },
      colors: colors,
      stroke: {
        width: [1, 0, 0],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          gradientToColors: ['#d7c29a', '#fff'],
          inverseColors: false,
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
        pattern: {
          style: 'verticalLines',
          width: 6,
          height: 6,
          strokeWidth: 2,
        },
      },
      xaxis: {
        tickAmount: 15,
        type: 'category',
        labels: {
          show: true,
          style: {
            colors: 'var(--text-slate)',
          },
        },
        formatter: function (value: any) {
          return value;
        },
        categories: dashboardSpendingSummaryStore.data.map((s) => DateUtils.formatDate(s.date)),
      },
      yaxis: {
        tickAmount: 4,
        labels: {
          show: true,
          style: {
            colors: ['var(--text-slate)'],
            fontSize: '14px',
            fontFamily: '"Proxima Nova", sans-serif',
            fontWeight: 500,
          },
          offsetX: -10,
          offsetY: 0,
          formatter: function (value: number) {
            if (value < 1000) return value.toString();
            return value / 1000 + 'k';
          },
        },
        axisTicks: {
          show: true,
          color: '#ededed',
          width: 16,
          offsetX: 13,
          offsetY: 1,
        },
        tooltip: {
          enabled: false,
          offsetX: 0,
        },
      },
      markers: {
        size: 5,
        strokeWidth: 1,
        fillOpacity: 0,
        strokeOpacity: 0,
        hover: {
          size: 8,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        x: {
          show: true,
          format: 'dd MMM yyyy',
          formatter: function (value) {
            const date = new Date(dashboardSpendingSummaryStore.data[value - 1].date).getDate();
            return t('card.spendingCurrency.day', { day: `${date}${getNumberPostfix(date, i18n.language)}` });
          },
        },
        y: {
          formatter: function (value) {
            if (symbol) return symbol + AmountUtils.transformToAmountWithCurrency(value);
            else return '';
          },
        },
      },
      grid: {
        show: true,
        borderColor: '#ededed',
        strokeDashArray: 10,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
    };
  }, [dashboardSpendingSummaryStore, currency, date, t]);
  const series = useMemo(
    () => [{ name: currency, type: 'area', data: dashboardSpendingSummaryStore.data.map((s) => s.amount) }],
    [dashboardSpendingSummaryStore, currency],
  );
  return (
    <div
      className={cn(
        'flex flex-col',
        PaletteUtils.isInsigniaPalette && 'gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && ['items-center', 'max-md:flex-col max-md:items-start'])}>
        <Text weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('card.spendingCurrency.title')}
        </Text>
        <div className={cn('flex gap-4', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select
            className='min-w-40'
            items={dates.map((d) => ({ value: d.value, label: `${d.name} ${new Date(d.value).getFullYear()}` }))}
            value={date}
            onChange={(val) => setDate(val!)}
          />
        </div>
      </div>
      <View
        className={cn(PaletteUtils.isVcfcPalette && 'mt-4')}
        loading={dashboardSpendingSummaryStore.loading}
        error={dashboardSpendingSummaryStore.error}
        onRefresh={() => $dashboardSpendingSummaryStore.events.getSpending({ currency, date })}
      >
        <div>
          <div
            className={cn(
              'relative z-10 -mb-[66px] px-[124px] py-4',
              PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-eerie-black',
              PaletteUtils.isVcfcPalette && 'mb-1 bg-transparent',
              styles['apexcharts-title-label'],
            )}
          >
            <span className={cn('after:border-b-primary', 'before:text-primary')}>{currency?.toUpperCase()}</span>
          </div>
          <Chart options={options} series={series} type='line' height={500} />
        </div>
      </View>
    </div>
  );
};
